import type { PartnerRole } from "@kundeninfo/api-client";
import { readJson, rememberValue } from "../../browserMemory.js";

/**
 * Die Wahl der Rollen-Chips in der Geschäftspartnerliste (Festlegung C9): welche Rollen die Liste zeigt und ob
 * Ehemalige dazukommen. Gemerkt wird sie im Browser wie andere Listenfilter — eine Bequemlichkeit des Geräts, keine
 * Einstellung des Kontos (Muster `panelSpeicher.ts`).
 */
export type RollenWahl = { roles: PartnerRole[]; includeFormer: boolean };

/** Die feste Folge der Chips und der Rollen in jeder Wahl. */
export const ROLLEN: readonly PartnerRole[] = ["CUSTOMER", "PROSPECT", "SUPPLIER"];

/** Vorgabe: alle drei Rollen an, Ehemalige aus (C9). */
export const ROLLEN_WAHL_VORGABE: RollenWahl = { roles: [...ROLLEN], includeFormer: false };

export const ROLLEN_SCHLUESSEL = "ki-partnerfilter";

function istRollenWahl(wert: unknown): wert is RollenWahl {
  if (typeof wert !== "object" || wert === null) return false;
  const k = wert as { roles?: unknown; includeFormer?: unknown };
  return Array.isArray(k.roles) && k.roles.length > 0
    && k.roles.every((r) => ROLLEN.includes(r as PartnerRole)) && typeof k.includeFormer === "boolean";
}

/** Die gemerkte Wahl, sonst die Vorgabe. Der Wert kommt aus dem Browser und wird geprüft (er kann fehlen, veraltet
 *  oder von Hand verändert sein); ein privates Fenster wirft schon beim Zugriff — dann gilt die Vorgabe. */
export function leseRollenWahl(): RollenWahl {
  const wert = readJson(ROLLEN_SCHLUESSEL);
  return istRollenWahl(wert)
    ? { roles: ROLLEN.filter((r) => wert.roles.includes(r)), includeFormer: wert.includeFormer }
    : ROLLEN_WAHL_VORGABE;
}

/** Merkt die Wahl für den nächsten Start; scheitert das, bleibt es folgenlos. */
export function merkeRollenWahl(w: RollenWahl): void {
  rememberValue(ROLLEN_SCHLUESSEL, JSON.stringify(w));
}

/** Schaltet eine Rolle um, in der festen Folge. Eine leere Wahl gibt es nicht: Der letzte gewählte Chip bleibt an. */
export function schalteRolle(w: RollenWahl, r: PartnerRole): RollenWahl {
  const an = w.roles.includes(r);
  if (an && w.roles.length === 1) return w;
  return { ...w, roles: ROLLEN.filter((x) => (x === r ? !an : w.roles.includes(x))) };
}

/** Die Kundenauswahl der Tickets fragt JEDE Rolle samt Ehemaligen: Ein Ticket kann jede Firma betreffen (Telekom-
 *  Störung) — ohne `role` fände sie nach P3 keine reinen Lieferanten. */
export const JEDE_ROLLE: { role: readonly PartnerRole[]; includeFormer: boolean } = { role: ROLLEN, includeFormer: true };
